import React, { useEffect, useRef, useState } from 'react';
import { BellIcon, GlobeIcon, MenuIcon, ChevronDownIcon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCity } from '../contexts/CityContext';
import { CITIES } from '../utils/activeCity';
import { useAccount } from '../contexts/AccountContext';
import { ACCOUNTS, type Account } from '../data/accounts';
function initialsOf(a: Account) {
  return `${a.firstName[0]}${a.lastName[0]}`.toUpperCase();
}
export function AdminHeader() {
  const {
    lang,
    setLang,
    t
  } = useLanguage();
  const {
    city,
    setCity
  } = useCity();
  const {
    account,
    setAccountId
  } = useAccount();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!accountOpen) return;
    const onPointer = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAccountOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [accountOpen]);
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return <header className="w-full bg-[#0F172A] text-white h-16 flex items-center justify-between px-6 shadow-md z-50 relative" data-id="element-19" data-mp="37vpaahnqx2et">
      <div className="flex items-center gap-4" data-id="element-20" data-mp="32w2zeeve3y96">
        <div className="lg:hidden" data-id="element-21" data-mp="32v9wjozjxswb">
          <MenuIcon className="w-6 h-6 text-slate-300" data-id="element-22" data-mp="3hma7c0nrtyvm" />
        </div>
        <div ref={menuRef} className="relative" data-id="element-23" data-mp="3cmxixbh94jqx">
          <button type="button" onClick={() => setOpen(v => !v)} aria-haspopup="listbox" aria-expanded={open} aria-label={t(`Ville : ${city}. Changer de ville`, `City: ${city}. Switch city`)} className="group flex items-center gap-1.5 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5]" data-id="element-24" data-mp="3co8mpbap5ri1">
            <span className="font-bold text-lg tracking-tight font-heading" data-id="element-25" data-mp="2tuuz5r9bcncg">
              {city}
            </span>
            <ChevronDownIcon className={`w-3.5 h-3.5 text-slate-500 group-hover:text-slate-300 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-26" data-mp="e3gyg15xty7s" />
          </button>

          {open && <ul role="listbox" aria-label={t('Changer de ville', 'Switch city')} className="absolute left-0 top-full mt-2 w-48 bg-white text-slate-900 rounded-lg border border-slate-200 shadow-lg py-1" data-id="element-27" data-mp="lpcdtplos4cy">
              {CITIES.map(c => <li key={c} data-id="element-28" data-mp="zfpkzpqr7f18">
                  <button type="button" role="option" aria-selected={city === c} onClick={() => {
              setCity(c);
              setOpen(false);
            }} className="w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-slate-50" data-id="element-29" data-mp="zhiq30lpxr74">
                    <span className={city === c ? 'font-semibold' : ''} data-id="element-30" data-mp="1idipuxkxtz19">
                      {t(`Ville de ${c}`, `City of ${c}`)}
                    </span>
                    {city === c && <CheckIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-31" data-mp="3vnwa2ype97pb" />}
                  </button>
                </li>)}
            </ul>}
        </div>
      </div>

      <div className="flex items-center gap-6" data-id="element-32" data-mp="3lo5fyy4qwupr">
        <button onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')} className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors" title={lang === 'fr' ? 'Passer en anglais' : 'Switch to French'} data-id="element-33" data-mp="3loyp25r6fwb2">
          <GlobeIcon className="w-5 h-5" data-id="element-34" data-mp="d8lsyguo48j5" />
          <span className="text-sm font-medium hidden sm:block" data-id="element-35" data-mp="daygran3xd44">
            {lang === 'fr' ? 'EN' : 'FR'}
          </span>
        </button>

        <button className="relative text-slate-300 hover:text-white transition-colors" data-id="element-36" data-mp="4h3cs5xq1sq2">
          <BellIcon className="w-5 h-5" data-id="element-37" data-mp="10y7pewjno9ng" />
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#4F46E5] rounded-full" data-id="element-38" data-mp="10z9brx64gikh"></span>
        </button>

        <div ref={accountRef} className="relative pl-4 border-l border-slate-700" data-id="element-39" data-mp="w66d5amhmixf">
          <button type="button" onClick={() => setAccountOpen(v => !v)} aria-haspopup="listbox" aria-expanded={accountOpen} aria-label={t(`Compte : ${account.firstName} ${account.lastName[0]}. Changer de compte`, `Account: ${account.firstName} ${account.lastName[0]}. Switch account`)} className="group flex items-center gap-2 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5]" data-id="element-40" data-mp="2evsrpn5zfolt">
            <div className="w-8 h-8 rounded-full bg-slate-700 group-hover:bg-slate-600 transition-colors flex items-center justify-center text-sm font-bold" data-id="element-41" data-mp="32q3nos2kq8fp">
              {initialsOf(account)}
            </div>
            <ChevronDownIcon className={`w-3.5 h-3.5 text-slate-500 group-hover:text-slate-300 transition-transform ${accountOpen ? 'rotate-180' : ''}`} data-id="element-42" data-mp="3gjutpqh3ja6o" />
          </button>

          {accountOpen && <ul role="listbox" aria-label={t('Changer de compte', 'Switch account')} className="absolute right-0 top-full mt-2 w-48 bg-white text-slate-900 rounded-lg border border-slate-200 shadow-lg py-1" data-id="element-43" data-mp="1lgy84s0okmsf">
              {ACCOUNTS.map(a => {
            const selected = a.id === account.id;
            return <li key={a.id} data-id="element-44" data-mp="245v1mto97b9l">
                    <button type="button" role="option" aria-selected={selected} onClick={() => {
                setAccountId(a.id);
                setAccountOpen(false);
              }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-slate-50" data-id="element-45" data-mp="2490t2fkcup8h">
                      <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-[10px] font-bold shrink-0" data-id="element-46" data-mp="esrouwgu42ga">
                        {initialsOf(a)}
                      </span>
                      <span className={`flex-1 ${selected ? 'font-semibold' : ''}`} data-id="element-47" data-mp="2vdd4d0a1hlzq">
                        {a.firstName} {a.lastName[0]}.
                      </span>
                      {selected && <CheckIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-48" data-mp="2lo3abevmxt8z" />}
                    </button>
                  </li>;
          })}
            </ul>}
        </div>
      </div>
    </header>;
}